/**
 * Tap to Pay on iPhone — native bridge orchestration.
 *
 * Reader-free in-person card collection (contactless card / Apple Pay / Google
 * Pay tapped directly on the operator's NFC iPhone). This is NATIVE-ONLY: it
 * requires the Stripe Terminal iOS SDK exposed through a Capacitor plugin
 * (`StripeTapToPay`). On the web (or any device without the plugin) it is
 * unavailable by design — the operator uses Email/SMS instead.
 *
 * Native plugin contract (`StripeTapToPay.collectPayment`):
 *   args: { connectionToken, locationId, clientSecret, merchantDisplayName, amountLabel }
 *   returns: { status: 'succeeded' | 'canceled' | 'failed', paymentIntentId, error? }
 */
import { Capacitor } from '@capacitor/core';

const getPlugin = () => {
  try {
    return (Capacitor && Capacitor.Plugins && Capacitor.Plugins.StripeTapToPay) || null;
  } catch {
    return null;
  }
};

export const isNativeIOS = () => {
  try {
    return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
  } catch {
    return false;
  }
};

/** True only when the device can actually accept a tap (native iOS + plugin present). */
export const isTapToPaySupported = () => isNativeIOS() && !!getPlugin();

/**
 * Run a full in-person balance collection.
 * @returns settle response from the backend on success.
 * @throws Error with `.code` ('unavailable' | 'canceled' | 'failed') on failure.
 */
export async function collectInPerson({ orgApi, booking, formatAmount, onStatus }) {
  const plugin = getPlugin();
  if (!isTapToPaySupported() || !plugin) {
    const err = new Error('Tap to Pay works in the BookingFlows iPhone app.');
    err.code = 'unavailable';
    throw err;
  }

  onStatus?.('Preparing reader…');
  const [tokenRes, configRes, intentRes] = await Promise.all([
    orgApi.terminalConnectionToken(),
    orgApi.terminalConfig(),
    orgApi.terminalCreateIntent(booking.id),
  ]);
  const { secret } = tokenRes.data || {};
  const config = configRes.data || {};
  const intent = intentRes.data || {};

  onStatus?.('Ready — ask the customer to tap');
  const result = await plugin.collectPayment({
    connectionToken: secret,
    locationId: config.locationId,
    clientSecret: intent.clientSecret,
    merchantDisplayName: config.merchantDisplayName || 'BookingFlows',
    amountLabel: formatAmount ? formatAmount(intent.amount) : String(intent.amount),
  });

  if (!result || result.status !== 'succeeded') {
    const err = new Error(result?.error || 'Payment was not completed.');
    err.code = result?.status || 'failed';
    throw err;
  }

  onStatus?.('Confirming payment…');
  const settle = await orgApi.terminalSettle(booking.id, result.paymentIntentId);
  return settle.data;
}
